import { Button } from '@design-system/components/button/button';
import { Input } from '@design-system/components/input/input';
import { Typography } from '@design-system/components/typography/typography';
import { KeyboardEvent, useEffect, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import { ReactComponent as LogoSvg } from '../../../assets/logo.svg';
import { ReactComponent as GoogleLogoSvg } from './assets/google-logo.svg';
import { ReactComponent as AppleLogoSvg } from './assets/apple-logo.svg';
import {
  LOGIN_EMAIL_PRIVACY_MESSAGE,
  type LoginViewModel,
} from '../view-model/login.view-model';
import { authVmCopies } from '../view-model/register.types';
import { testimonialsData } from './testimonials.data';
import * as styles from './login.view.css';
import { canDetectAuthViewport, isAuthDesktopViewport } from './viewport';
import { AuthLayout } from './components/AuthLayout';
import { AuthClientAppHint } from './components/AuthFooterText';
import { TestimonialsCarousel } from './components/TestimonialsCarousel';

const SIGNIN_ERROR_TOAST_ID = 'registration-signin-error-toast';
const SIGNIN_INVALID_EMAIL_TOAST_ID = 'signin-invalid-email-toast';
const SIGNIN_TITLE = 'Welcome back!';
const SIGNIN_SUBTITLE =
  "We'll send you a code to sign in - no password required!";
const SIGNIN_FOOTER_PROMPT = "Don't have account?";
const SIGNIN_EMAIL_ARIA_LABEL = 'Signin email';
const SIGNIN_INLINE_ERROR_MESSAGE =
  "We don't recognize this email. Check your spelling";
const SIGNIN_INVALID_EMAIL_TOAST_MESSAGE =
  'Please enter a valid email address.';
const SIGNIN_INLINE_ERROR_CTA_LABEL = 'create an account.';
const SIGNIN_TOAST_GENERIC_ERROR_MESSAGE =
  'Something went wrong. Please try again.';
const SIGNIN_REGISTER_CTA_TEXT = 'Register';
const SIGNIN_TOAST_REGISTER_BUTTON_TEST_ID = 'signin-error-register-button';

type SignInToastConfig = {
  message: string;
  ctaLabel?: string;
};

const toSignInToastConfig = (submitErrorMessage: string): SignInToastConfig => {
  if (submitErrorMessage === LOGIN_EMAIL_PRIVACY_MESSAGE) {
    return {
      message: SIGNIN_INLINE_ERROR_MESSAGE,
      ctaLabel: SIGNIN_REGISTER_CTA_TEXT,
    };
  }

  const trimmedMessage = submitErrorMessage.trim();
  return {
    message:
      trimmedMessage.length > 0
        ? trimmedMessage
        : SIGNIN_TOAST_GENERIC_ERROR_MESSAGE,
  };
};

export const LoginView = (loginVM: LoginViewModel) => {
  const previousErrorRef = useRef<string | null>(null);
  const emailInputRef = useRef<HTMLInputElement | null>(null);
  const [isEmailTouched, setIsEmailTouched] = useState(false);
  const errorMessage =
    loginVM.state.submitState.type === 'fetch-error'
      ? loginVM.state.submitState.value
      : null;

  useEffect(() => {
    if (!canDetectAuthViewport()) {
      return;
    }

    if (!isAuthDesktopViewport()) {
      return;
    }

    emailInputRef.current?.focus();
  }, []);

  useEffect(() => {
    if (!errorMessage) {
      previousErrorRef.current = null;
      toast.dismiss(SIGNIN_ERROR_TOAST_ID);
      return;
    }

    if (previousErrorRef.current === errorMessage) {
      return;
    }

    previousErrorRef.current = errorMessage;
    const toastConfig = toSignInToastConfig(errorMessage);

    toast.custom(
      (toastItem) => (
        <div className={styles.errorToast}>
          <div className={styles.errorToastLeading}>
            <span className={styles.errorToastIcon} aria-hidden>
              ✖
            </span>
            <Typography
              variant="body"
              size="medium"
              className={styles.errorToastText}
            >
              {toastConfig.message}
            </Typography>
          </div>
          {toastConfig.ctaLabel ? (
            <Button
              type="button"
              variant="text"
              color="primary"
              size="large"
              testId={SIGNIN_TOAST_REGISTER_BUTTON_TEST_ID}
              className={styles.errorToastButton}
              onClick={() => {
                loginVM.dispatch('goToSignUp');
                toast.dismiss(toastItem.id);
              }}
            >
              {toastConfig.ctaLabel}
            </Button>
          ) : null}
        </div>
      ),
      {
        id: SIGNIN_ERROR_TOAST_ID,
        duration: 5000,
        position: 'top-right',
      },
    );
  }, [errorMessage, loginVM]);

  const { state, dispatch } = loginVM;
  const hasInvalidTouchedEmail =
    isEmailTouched && state.email.trim().length > 0 && !state.canContinue;
  const hasInlineError = hasInvalidTouchedEmail || Boolean(errorMessage);
  const isSubmitting = state.submitState.type === 'fetching';
  const isGoogleSsoLoading = state.googleSsoState.type === 'loading';
  const googleSsoError =
    state.googleSsoState.type === 'error' ? state.googleSsoState.message : null;
  const isAppleSsoLoading = state.appleSsoState.type === 'loading';
  const appleSsoError =
    state.appleSsoState.type === 'error' ? state.appleSsoState.message : null;
  const emailInputWrapperClassName = hasInlineError
    ? styles.authInputWrapperError
    : styles.authInputWrapper;

  useEffect(() => {
    if (!hasInvalidTouchedEmail) {
      toast.dismiss(SIGNIN_INVALID_EMAIL_TOAST_ID);
      return;
    }

    toast.custom(
      () => (
        <div className={styles.errorToast}>
          <div className={styles.errorToastLeading}>
            <span className={styles.errorToastIcon} aria-hidden>
              ✖
            </span>
            <Typography
              variant="body"
              size="medium"
              className={styles.errorToastText}
            >
              {SIGNIN_INVALID_EMAIL_TOAST_MESSAGE}
            </Typography>
          </div>
        </div>
      ),
      {
        id: SIGNIN_INVALID_EMAIL_TOAST_ID,
        duration: 5000,
        position: 'top-right',
      },
    );
  }, [hasInvalidTouchedEmail]);

  const submitSignIn = (): void => {
    dispatch('continue');
  };

  const handleEmailInputKeyDown = (
    event: KeyboardEvent<HTMLInputElement>,
  ): void => {
    if (event.key !== 'Enter') {
      return;
    }

    event.preventDefault();
    submitSignIn();
  };

  return (
    <div className={styles.pageTransition}>
      <AuthLayout
        rightColumn={
          <section
            className={styles.testimonialsSection}
            aria-label={authVmCopies.common.creatorTestimonialsAriaLabel}
          >
            <TestimonialsCarousel testimonials={testimonialsData} />
          </section>
        }
        boxClassName={styles.formBox}
        header={<LogoSvg className={styles.brandLogo} />}
        bodyClassName={styles.centeredBody}
        title={
          <Typography
            variant="headline"
            size="large"
            color="N-100"
            className={styles.titleText}
          >
            {SIGNIN_TITLE}
          </Typography>
        }
        subtitle={
          <Typography
            variant="body"
            size="large"
            className={styles.subtitleText}
          >
            {SIGNIN_SUBTITLE}
          </Typography>
        }
        cta={
          <>
            <Button
              type="button"
              color="neutral"
              size="large"
              testId="signin-continue-button"
              loading={isSubmitting}
              disabled={!state.canContinue || isSubmitting}
              className={styles.continueButton}
              onClick={submitSignIn}
            >
              {authVmCopies.common.continue}
            </Button>

            <div className={styles.orSeparator}>
              <div className={styles.orSeparatorLine} />
              <span className={styles.orSeparatorText}>or</span>
              <div className={styles.orSeparatorLine} />
            </div>

            <div className={styles.ssoButtonsStack}>
              <button
                type="button"
                data-testid="signin-google-sso-button"
                className={styles.googleSsoButton}
                disabled={isGoogleSsoLoading}
                onClick={() => dispatch('googleSso')}
              >
                <GoogleLogoSvg className={styles.googleSsoIcon} />
                {isGoogleSsoLoading ? 'Signing in…' : 'Sign in with Google'}
              </button>

              <button
                type="button"
                data-testid="signin-apple-sso-button"
                className={styles.appleSsoButton}
                disabled={isAppleSsoLoading}
                onClick={() => dispatch('appleSso')}
              >
                <AppleLogoSvg className={styles.appleSsoIcon} />
                {isAppleSsoLoading ? 'Signing in…' : 'Sign in with Apple'}
              </button>
            </div>

            {googleSsoError ? (
              <Typography
                role="alert"
                variant="body"
                size="small"
                className={styles.googleSsoError}
              >
                {googleSsoError}
              </Typography>
            ) : null}

            {appleSsoError ? (
              <Typography
                role="alert"
                variant="body"
                size="small"
                className={styles.appleSsoError}
              >
                {appleSsoError}
              </Typography>
            ) : null}
          </>
        }
        footer={
          <>
            <div className={styles.divider} />

            <div className={styles.footerCta}>
              <Typography
                variant="body"
                size="medium"
                className={styles.footerPrompt}
              >
                {SIGNIN_FOOTER_PROMPT}
              </Typography>
              <Button
                type="button"
                variant="text"
                color="primary"
                size="large"
                testId="signin-register-button"
                className={styles.registerButton}
                onClick={() => dispatch('goToSignUp')}
              >
                {SIGNIN_REGISTER_CTA_TEXT}
              </Button>
            </div>

            <AuthClientAppHint
              hintClassName={styles.bottomHint}
              linkClassName={styles.textLink}
            />
          </>
        }
      >
        <div data-testid="signin-form" className={styles.form}>
          <Input
            ref={emailInputRef}
            aria-label={SIGNIN_EMAIL_ARIA_LABEL}
            type="email"
            placeholder={authVmCopies.common.emailAddressPlaceholder}
            value={state.email}
            testId="signin-email-input"
            error={hasInlineError ? SIGNIN_INLINE_ERROR_MESSAGE : undefined}
            hideError
            wrapperClassName={styles.authInputRoot}
            inputWrapperClassName={emailInputWrapperClassName}
            onChange={(e) => {
              setIsEmailTouched(false);
              dispatch('setEmail', e.currentTarget.value);
            }}
            onBlur={() => setIsEmailTouched(true)}
            onKeyDown={handleEmailInputKeyDown}
          />

          {hasInlineError ? (
            <Typography
              role="alert"
              variant="body"
              size="small"
              className={styles.errorText}
            >
              {SIGNIN_INLINE_ERROR_MESSAGE}
              {' or '}
              <button
                type="button"
                className={styles.errorLinkButton}
                onClick={() => dispatch('goToSignUp')}
              >
                {SIGNIN_INLINE_ERROR_CTA_LABEL}
              </button>
            </Typography>
          ) : null}
        </div>
      </AuthLayout>
    </div>
  );
};
